// Turnover Control Tower — pure coverage/classification helpers (Sprint 6).
//
// Extracted from `TurnoverControlTower.tsx` so the demand-side coverage logic
// (coperti / da confermare / non pianificati / a rischio) is unit-testable
// without a browser or a live session. No React, no IO — same discipline as
// `src/lib/scheduler/rules.ts`.

import { evaluateRowFlags, type RowRiskFlags } from "@/lib/scheduler";

export type Coverage = "coperti" | "da_confermare" | "non_pianificati";

/** Minimal platform-booking shape the coverage helpers need. */
export interface CoverageBookingInput {
  status: string | null;
  scheduled_date: string | null;
  scheduled_time: string | null;
  window_start_at: string | null;
  window_end_at: string | null;
  checkout_at: string | null;
  checkin_at: string | null;
}

export interface CoverageCounters {
  coperti: number;
  da_confermare: number;
  non_pianificati: number;
  a_rischio: number;
}

export const COVERAGE_LABEL: Record<Coverage | "a_rischio", string> = {
  coperti: "Coperti",
  da_confermare: "Da confermare",
  non_pianificati: "Non pianificati",
  a_rischio: "A rischio",
};

export function isCancelled(status: string | null | undefined): boolean {
  return String(status ?? "").toLowerCase() === "cancelled";
}

/** Map a single platform booking to a coverage bucket. */
export function coverageOf(b: CoverageBookingInput): Coverage {
  if (!b.scheduled_date || !b.scheduled_time) return "non_pianificati";
  const s = String(b.status ?? "").toLowerCase();
  if (s === "pending") return "da_confermare";
  return "coperti";
}

/** Per-row scheduler flags for a platform booking. */
export function rowFlagsOf(b: CoverageBookingInput, now: number = Date.now()): RowRiskFlags {
  return evaluateRowFlags(
    {
      source: "platform",
      window_start_at: b.window_start_at,
      window_end_at: b.window_end_at,
      scheduled_date: b.scheduled_date,
      scheduled_time: b.scheduled_time,
      checkout_at: b.checkout_at,
      checkin_at: b.checkin_at,
      scheduling_precision: "platform",
    },
    now,
  );
}

/**
 * Portfolio-level coverage counters. Cancelled bookings are excluded.
 * `a_rischio` counts rows flagged urgent or with a tight (<=2h) window.
 */
export function computeCoverageCounters(
  bookings: CoverageBookingInput[],
  now: number = Date.now(),
): CoverageCounters {
  const acc: CoverageCounters = { coperti: 0, da_confermare: 0, non_pianificati: 0, a_rischio: 0 };
  for (const b of bookings) {
    if (isCancelled(b.status)) continue;
    acc[coverageOf(b)] += 1;
    const f = rowFlagsOf(b, now);
    if (f.urgent || f.tight_window) acc.a_rischio += 1;
  }
  return acc;
}
